import { t as debounce } from "./app.shared-OQDvbu1B.js";

var HeaderScrollBehavior = class {
    constructor(host) {
        this.host = host;
        this.state = {
            atTop: true,
            announcementVisible: false,
            direction: "up",
            lastScrollY: 0,
            scrollDelta: 0,
            hovering: false
        };
        this.scrolledAway = window.scrollY > 0;
        this.solidSwapTimer = null;
        this.suppressScrollUntil = 0;
        this.transparentOptIn = Boolean(document.querySelector("main > .shopify-section:first-child [allow-transparent-header]"));
        this.mqHover = window.matchMedia("(hover: none)");
        this.level0Toggle = this.host.querySelector("#menu-level0");
        this.onScroll = this.onScroll.bind(this);
        this.onSuppressScroll = this.onSuppressScroll.bind(this);
        this.onAtTopChange = this.onAtTopChange.bind(this);
        this.onAnnouncementChange = this.onAnnouncementChange.bind(this);
        this.onMouseEnter = this.onMouseEnter.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);
        this.onHoverMediaChange = this.onHoverMediaChange.bind(this);
        this.onDrawerChange = this.update.bind(this);
        this.update = this.update.bind(this);
        const HOVER_COMMIT_DELAY = 150;
        this.scheduleHoverCommit = debounce(() => {
            this.state.hovering = true;
            this.update();
        }, HOVER_COMMIT_DELAY);
        this.init();
    }
    setHeaderState(next) {
        if (this.host.dataset.headerState === next) return;
        this.host.dataset.headerState = next;
        document.documentElement.dataset.headerState = next;
    }
    init() {
        this.setHeaderState("visible");
        this.host.dataset.headerScheme = this.computeScheme();
        this.state.lastScrollY = window.scrollY;
        const sentinel = document.querySelector("[data-scroll-trigger]");
        if (sentinel) {
            this.atTopObserver = new IntersectionObserver(this.onAtTopChange);
            this.atTopObserver.observe(sentinel);
        }
        const announcement = document.querySelector("[data-announcement]");
        if (announcement && announcement.offsetHeight > 0) {
            this.state.announcementVisible = true;
            this.announcementObserver = new IntersectionObserver(this.onAnnouncementChange);
            this.announcementObserver.observe(announcement);
        }
        window.addEventListener("scroll", this.onScroll, {
            passive: true
        });
        document.addEventListener("global-ui:suppress-header-scroll", this.onSuppressScroll);
        this.updateHoverListeners();
        this.mqHover.addEventListener("change", this.onHoverMediaChange);
        this.menuObserver = new MutationObserver(this.update);
        this.menuObserver.observe(this.host, {
            subtree: true,
            attributes: true,
            attributeFilter: [ "open" ]
        });
        this.level0Toggle?.addEventListener("change", this.onDrawerChange);
        this.update();
    }
    onScroll() {
        const y = window.scrollY;
        if (performance.now() < this.suppressScrollUntil) {
            this.state.lastScrollY = y;
            this.state.scrollDelta = 0;
            return;
        }
        const direction = y > this.state.lastScrollY ? "down" : "up";
        if (direction === "down") this.state.scrollDelta += y - this.state.lastScrollY; else this.state.scrollDelta = 0;
        this.state.direction = direction;
        this.state.lastScrollY = y;
        this.update();
    }
    onSuppressScroll(event) {
        const duration = Number(event.detail?.duration) || 0;
        this.suppressScrollUntil = Math.max(this.suppressScrollUntil, performance.now() + duration);
    }
    onAtTopChange([entry]) {
        this.state.atTop = entry.isIntersecting;
        document.body.classList.toggle("is-scrolled", !entry.isIntersecting);
        window.dispatchEvent(new CustomEvent("layout:change"));
        this.update();
    }
    onAnnouncementChange([entry]) {
        this.state.announcementVisible = entry.isIntersecting;
        this.update();
    }
    onMouseEnter() {
        this.scheduleHoverCommit();
    }
    onMouseLeave() {
        this.scheduleHoverCommit.cancel();
        this.state.hovering = false;
        this.update();
    }
    onHoverMediaChange() {
        this.scheduleHoverCommit.cancel();
        this.state.hovering = false;
        this.updateHoverListeners();
        this.update();
    }
    updateHoverListeners() {
        if (!this.mqHover.matches) {
            this.host.addEventListener("mouseenter", this.onMouseEnter, {
                passive: true
            });
            this.host.addEventListener("mouseleave", this.onMouseLeave, {
                passive: true
            });
        } else {
            this.host.removeEventListener("mouseenter", this.onMouseEnter);
            this.host.removeEventListener("mouseleave", this.onMouseLeave);
            this.scheduleHoverCommit?.cancel();
        }
    }
    update() {
        const newState = this.computeState();
        const currentState = this.host.dataset.headerState;
        if (newState === "hidden" && currentState !== "hidden") {
            this.setHeaderState("hidden");
            this.scrolledAway = true;
            clearTimeout(this.solidSwapTimer);
            this.solidSwapTimer = setTimeout(() => {
                if (this.host.dataset.headerState === "hidden") this.host.dataset.headerScheme = "solid";
            }, 300);
            return;
        }
        if (newState === "visible" && currentState === "hidden") {
            this.setHeaderState("visible");
            clearTimeout(this.solidSwapTimer);
        } else if (currentState === "hidden") return;
        if (this.state.atTop) this.scrolledAway = false;
        const scheme = this.computeScheme();
        if (scheme !== this.host.dataset.headerScheme) this.host.dataset.headerScheme = scheme;
    }
    computeState() {
        const {atTop: atTop, direction: direction, announcementVisible: announcementVisible, scrollDelta: scrollDelta} = this.state;
        let result = "visible";
        if (!atTop && direction === "down" && !announcementVisible && scrollDelta > 10) result = "hidden";
        return result;
    }
    computeScheme() {
        if (!this.transparentOptIn) return "solid";
        if (this.forceSolid()) return "solid";
        if (this.scrolledAway) return "solid";
        return "transparent";
    }
    forceSolid() {
        return this.state.hovering || Boolean(this.host.querySelector("details.header--level1[open]")) || Boolean(this.level0Toggle?.checked);
    }
};

var site_header_default = (() => {
    if (!customElements.get("site-header")) customElements.define("site-header", class SiteHeader extends HTMLElement {
        constructor() {
            super();
            this.state = {
                isDesktop: false,
                isTablet: false
            };
            this.elements = this.initializeElements();
            this.mediaQueries = this.initializeMediaQueries();
            this.bindMethods();
            this.setupLoadingSpinnerReset();
            this.init();
        }
        initializeElements() {
            const elements = {
                header: this.closest("header"),
                level0Toggle: this.querySelector("#menu-level0"),
                level0: this.querySelector('[data-locker-container="menu-level0"]'),
                level1Details: this.querySelectorAll("details.header--level1"),
                level1Toggles: this.querySelectorAll("details.header--level1 summary.header--level1"),
                actionBarEl: this.querySelector(".header__action-bar"),
                mobileNavTabs: this.querySelector("mobile-nav-tabs")
            };
            elements.searchLabel = elements.actionBarEl?.querySelector('label[for="SearchDrawer"]');
            elements.cartLabel = elements.actionBarEl?.querySelector('label[for="Cart"]');
            elements.drawerSearchLabel = elements.level0?.querySelector('label[for="SearchDrawer"]');
            elements.drawerCartLabel = elements.level0?.querySelector('label[for="Cart"]');
            return elements;
        }
        initializeMediaQueries() {
            return {
                desktop: window.matchMedia("(min-width: 1025px)"),
                tablet: window.matchMedia("(max-width: 1024px)")
            };
        }
        bindMethods() {
            this.handleLevel0Toggle = this.handleLevel0Toggle.bind(this);
            this.handleOutsideInteraction = this.handleOutsideInteraction.bind(this);
            this.debouncedHandleEscape = debounce(this.handleEscape.bind(this), 100);
            this.handleActionBarClick = this.handleActionBarClick.bind(this);
        }
        handleLevel0Toggle(e) {
            const isToggled = e.target.checked;
            if (this.state.isTablet && isToggled) {
                (this.elements.level0?.querySelector("a, button, label, summary"))?.focus();
                if (this.elements.level0) this.elements.level0.scrollTop = 0;
                this.elements.mobileNavTabs?.reset();
                const openDetail = Array.from(this.elements.level1Details).find(detail => detail.hasAttribute("open"));
                if (openDetail) openDetail.removeAttribute("open");
            }
        }
        handleEscape(e) {
            if (e.key === "Escape") if (this.elements.level0Toggle?.checked) this.elements.level0Toggle.click(); else this.closeAllLevel1Menus();
        }
        handleOutsideInteraction(e) {
            if (this.contains(e.target)) return;
            if ([ "[data-locker]", ".search-drawer", ".cart-drawer", "#SearchDrawer", "#Cart" ].some(selector => {
                const element = document.querySelector(selector);
                return element && element.contains(e.target);
            })) return;
            if (this.elements.level0Toggle?.checked) this.elements.level0Toggle.click();
            this.closeAllLevel1Menus();
        }
        closeAllLevel1Menus() {
            this.querySelectorAll("details.header--level1[open]").forEach(menu => {
                menu.removeAttribute("open");
            });
        }
        addOutsideInteractionListeners() {
            document.addEventListener("click", this.handleOutsideInteraction, true);
            document.addEventListener("focusin", this.handleOutsideInteraction, true);
        }
        handleActionBarClick() {
            if (this.state.isTablet && this.elements.level0Toggle?.checked) this.elements.level0Toggle.click();
        }
        setupDesktopBehavior() {
            if (!this.state.isDesktop) return;
            if (this.elements.level0Toggle?.checked) {
                this.elements.level0Toggle.click();
                this.elements.level1Toggles[0]?.click();
            }
            this.style.position = null;
            this.setLevelContainerAttributes(true);
            document.removeEventListener("keydown", this.debouncedHandleEscape);
            this.elements.searchLabel?.removeEventListener("click", this.handleActionBarClick);
            this.elements.cartLabel?.removeEventListener("click", this.handleActionBarClick);
            this.elements.drawerSearchLabel?.removeEventListener("click", this.handleActionBarClick);
            this.elements.drawerCartLabel?.removeEventListener("click", this.handleActionBarClick);
            if (this.elements.actionBarEl) this.elements.actionBarEl.style.height = "";
        }
        setupLoadingSpinnerReset() {
            if (!this.elements.level0Toggle) return;
            this._lastNudge = null;
            const dispatchNudge = detail => {
                this._lastNudge = detail;
                document.dispatchEvent(new CustomEvent("global-ui:nudge", {
                    detail: detail
                }));
            };
            const handleNudgeRequest = event => {
                const {lane: lane} = event.detail ?? {};
                if (!this._lastNudge) return;
                if (lane !== this._lastNudge.lane && lane !== "all") return;
                document.dispatchEvent(new CustomEvent("global-ui:nudge", {
                    detail: this._lastNudge
                }));
            };
            document.addEventListener("global-ui:nudge-request", handleNudgeRequest);
            const handleToggle = () => {
                if (!this.state.isTablet) return;
                dispatchNudge({
                    lane: "all",
                    source: "site-header",
                    transform: this.elements.level0Toggle.checked ? "RESET" : ""
                });
            };
            this.elements.level0Toggle.addEventListener("change", handleToggle);
            this._cleanupSpinnerReset = () => {
                this.elements.level0Toggle.removeEventListener("change", handleToggle);
                document.removeEventListener("global-ui:nudge-request", handleNudgeRequest);
            };
        }
        setupTabletBehavior() {
            if (!this.state.isTablet) return;
            this.querySelectorAll("details").forEach(detail => detail.removeAttribute("open"));
            if (this.elements.level0Toggle?.checked) this.elements.level0Toggle.click();
            this.setLevelContainerAttributes(false);
            document.addEventListener("keydown", this.debouncedHandleEscape, {
                passive: true
            });
            this.elements.searchLabel?.addEventListener("click", this.handleActionBarClick);
            this.elements.cartLabel?.addEventListener("click", this.handleActionBarClick);
            this.elements.drawerSearchLabel?.addEventListener("click", this.handleActionBarClick);
            this.elements.drawerCartLabel?.addEventListener("click", this.handleActionBarClick);
            requestAnimationFrame(() => {
                const headerHeight = this.offsetHeight;
                if (this.elements.actionBarEl) this.elements.actionBarEl.style.height = `${headerHeight}px`;
            });
        }
        setLevelContainerAttributes(isVisible) {
            this.elements.level1Details.forEach(toggle => {
                const containerId = `MegaMenu-Content-${toggle.id.split("-").pop()}`;
                const container = toggle.parentElement.querySelector(`#${containerId}`);
                if (container) {
                    container.setAttribute("aria-hidden", !isVisible);
                    if (isVisible) container.removeAttribute("tabindex"); else container.setAttribute("tabindex", "-1");
                }
            });
        }
        handleMediaQueryChange() {
            const isNowDesktop = this.mediaQueries.desktop.matches;
            if (isNowDesktop !== this.state.isDesktop) {
                this.state.isDesktop = isNowDesktop;
                this.state.isTablet = !isNowDesktop;
                if (this.state.isDesktop) this.setupDesktopBehavior(); else this.setupTabletBehavior();
            }
        }
        init() {
            this.state.isDesktop = this.mediaQueries.desktop.matches;
            this.state.isTablet = this.mediaQueries.tablet.matches;
            this.elements.level0Toggle?.addEventListener("change", this.handleLevel0Toggle);
            this.addOutsideInteractionListeners();
            this.mediaQueries.desktop.addEventListener("change", () => this.handleMediaQueryChange());
            if (this.state.isDesktop) this.setupDesktopBehavior(); else this.setupTabletBehavior();
            this.scrollBehavior = new HeaderScrollBehavior(this);
        }
    });
})();